// `notification_type` → the list page a TEAM/TASK/GAG notification opens when
// it carries no `item_id` (a legacy row predating 2026-10-02, or a sender that
// genuinely has none). Keyed here rather than inlined so the two tables
// (prefix → page, below) and this one cannot silently disagree about which
// types exist.
// 🔀 2026-10-02 (Gags/teams/tasks Stage 1): Manage Teams is HIDDEN (its route
// redirects to My Tasks), and every old team invite / team notification type is
// RETIRED (spec) — nothing on a Stage 1 surface writes them any more. A legacy
// row still opens SOMEWHERE real: My Tasks, never the hidden page.
const TEAM_NOTIFICATION_FALLBACK = '/teams/my-tasks';
const TASK_NOTIFICATION_FALLBACK = '/teams/my-tasks';
const GAG_NOTIFICATION_FALLBACK = '/teams/breakdown';

// Every non-order, non-hire type this app's server writes into
// `notifications.notification_type` that is ABOUT a team, a task or a gag —
// see `server/routes/teams.js#sendTaskNotification`,
// `server/routes/gags.js#sendGagNotification` and
// `server/helpers/gagOwnerNotice.js`. Each maps to the deep-link QUERY KEY its
// destination page already reads (`TeamManage.js`'s `?teamId=`,
// `MyTeamTasks.js`'s `?task=`, `GagBreakdown.js`'s `?gag=` via
// `useDeepLinkParam`) plus that page's own route, so a notification with an
// `item_id` opens the exact row and one with none still lands on the right
// LIST rather than nowhere.
const TEAM_NOTIFICATION_TYPES = {
  // Retired types (see the fallback note above) — their item_id is a legacy
  // TEAM id that no Stage 1 page can open, so they land on the list.
  team_member_invited: { param: null, path: '/teams/my-tasks' },
  team_invite_accepted: { param: null, path: '/teams/my-tasks' },
};
const TASK_NOTIFICATION_TYPES = {
  task_assigned: { param: 'task', path: '/teams/my-tasks' },
  task_completed: { param: 'task', path: '/teams/my-tasks' },
  task_status_changed: { param: 'task', path: '/teams/my-tasks' },
  task_comment: { param: 'task', path: '/teams/my-tasks' },
};
const GAG_NOTIFICATION_TYPES = {
  gag_assigned: { param: 'gag', path: '/teams/breakdown' },
  gag_task_added: { param: 'gag', path: '/teams/breakdown' },
  gag_task_completed: { param: 'gag', path: '/teams/breakdown' },
  gag_shoot_date_changed: { param: 'gag', path: '/teams/breakdown' },
  gag_reassigned: { param: 'gag', path: '/teams/breakdown' },
  // Stage 1 (2026-10-02): "You've been added to the team on <gag>" → opens the gag.
  gag_team_added: { param: 'gag', path: '/teams/breakdown' },
};

/**
 * Where a bell notification opens (2026-09-27, owner A3: *"hire notifications
 * open the hire"*; extended 2026-10-02 for the "added to a team" dead click —
 * owner, on staging: *"didn't take me anywhere"*).
 *
 * - An ORDER notification opens the order EXPANDED in a list (2026-10-09 —
 *   see the branch at the foot): Department Orders, My Orders or To Approve.
 *   It reaches `/orders/<order_id>` only in the no-seat case below.
 * - A HIRE notification (`hire_*` type carrying an `item_id`) opens the hire on
 *   the hires Table through its `?hire=<item_id>` deep link: Department Hires
 *   when the viewer can see department orders in the department they will be
 *   standing in (the notification's own department — the bell switches to it —
 *   or the active one when it carries none), My Hires otherwise. Decided by the
 *   CAPABILITY, never `isBuyer` — the same `canViewDepartmentOrders` question
 *   `MyHires.js`'s `canViewDeptWide` and its "View order" routing ask.
 * - A hire in a department the viewer holds NO seat in: the bell cannot switch
 *   them there, and both hires pages list only the active department, so a
 *   `?hire=` link would land on "could not be found". It falls back to
 *   `/orders/<order_id>` — what EVERY order notification does in that case
 *   (the order page is per-resource and gates on the order's own department).
 * - A TEAM / TASK / GAG notification (`team_member_invited`,
 *   `team_invite_accepted`, `task_assigned`, `task_completed`,
 *   `task_status_changed`, `task_comment`, `gag_assigned`, `gag_task_added`,
 *   `gag_task_completed`, `gag_shoot_date_changed`, `gag_reassigned`) opens its
 *   row's deep link when `item_id` is set (`?teamId=`/`?task=`/`?gag=` on
 *   `/teams/manage`, `/teams/my-tasks`, `/teams/breakdown` respectively — each
 *   param is read by the page that already owns it, never invented here), and
 *   the bare LIST page otherwise — a legacy row from before `item_id` carried
 *   one of these ids still goes somewhere real, never nowhere.
 * - `department_invite` is deliberately NOT handled here: `Layout.js`'s
 *   `handleNotificationClick` branches on it BEFORE calling this function (it
 *   opens the Join prompt, not a navigation), so it never reaches this code.
 * - Every other type this server writes (`department_warning`,
 *   `grading_reference` is the one exception — see below) carries no
 *   identifying data at all (no order, no item, no department column on
 *   `notifications`) and genuinely has nowhere to open: this returns `null`,
 *   and the caller must not render the row as clickable (see
 *   `Layout.js`'s `NotificationRow`).
 * - `grading_reference` is id-independent by design — it always opens
 *   `/grading`, the viewer's own grading profile, because the server inserts
 *   it addressed to `request.requested_by_user_id` alone and there is only
 *   ever one such page per viewer.
 *
 * 🔻 REVERTS a round-4 (bd8fd420) contract change for an UNSEATED SITE ADMIN,
 *    an adversarial reviewer's jsdom probe disproving the premise it shipped
 *    on. bd8fd420 read "an admin holds no seats, so `?hire=` would land on
 *    'could not be found'" and sent every unseated admin to the order page.
 *    That premise is FALSE for an admin's DEFAULT (no active department)
 *    state: `DepartmentContext.js` sets `activeDepartment` to `null` for
 *    EVERY admin at load (`if (data.isAdmin) { … setActiveDepartment(null);
 *    }`); `DepartmentHires.js`'s `fetchData` sends `department_id` ONLY when
 *    `activeDepartmentId != null`; server `buildDeptFilter`
 *    (`server/routes/hires.js`) returns an UNFILTERED clause for a global
 *    admin — so the list IS instance-wide and `?hire=` resolves and expands.
 *    The genuine dead end is narrower: an admin STANDING IN a department
 *    (`activeDepartmentId` set) whose hire notification names a DIFFERENT
 *    department — there the bell cannot switch them and the list is scoped
 *    to the department they stand in, so the row is unreachable.
 * Pure: the bell passes what it knows; nothing here reads a context.
 */
export function notificationTarget(notification, {
  isAdmin = false, assignments = [], activeDepartmentId = null, capabilitiesFor, userId = null,
} = {}) {
  const n = notification || {};
  const type = String(n.notification_type || '');

  if (type === 'grading_reference') return '/grading';

  if (TEAM_NOTIFICATION_TYPES[type]) {
    const { param, path } = TEAM_NOTIFICATION_TYPES[type];
    return param && n.item_id != null ? `${path}?${param}=${n.item_id}` : TEAM_NOTIFICATION_FALLBACK;
  }
  if (TASK_NOTIFICATION_TYPES[type]) {
    const { param, path } = TASK_NOTIFICATION_TYPES[type];
    return n.item_id != null ? `${path}?${param}=${n.item_id}` : TASK_NOTIFICATION_FALLBACK;
  }
  if (GAG_NOTIFICATION_TYPES[type]) {
    const { param, path } = GAG_NOTIFICATION_TYPES[type];
    return n.item_id != null ? `${path}?${param}=${n.item_id}` : GAG_NOTIFICATION_FALLBACK;
  }

  const isHire = n.item_id != null && String(n.notification_type || '').startsWith('hire_');
  if (!isHire && !n.order_id) return null;

  const deptId = n.department_id != null ? n.department_id : activeDepartmentId;
  // An admin is "seated" in every department that is EITHER the one they
  // stand in (activeDepartmentId matches, or they stand nowhere at all —
  // `buildDeptFilter`'s admin arm is instance-wide there) — never a
  // department admission derived from `assignments`, which is always empty
  // for an admin (see the docblock above).
  const seated = n.department_id == null
    || (assignments || []).some((a) => a.department_id === n.department_id)
    || (isAdmin && (activeDepartmentId == null || activeDepartmentId === n.department_id));
  // No seat → no list can show the row (the bell cannot switch them there), so
  // the per-resource order page is the one place left. The ONLY remaining
  // route to `/orders/:id` from the bell.
  if (!seated) return n.order_id ? `/orders/${n.order_id}` : null;
  // An order notification with NO department (a legacy unscoped order, or a
  // deleted one — the bell's LEFT JOIN yields null) belongs to no list the
  // viewer is standing in, so it keeps the per-resource page (review M1).
  if (!isHire && n.department_id == null) return n.order_id ? `/orders/${n.order_id}` : null;

  const caps = typeof capabilitiesFor === 'function' ? (capabilitiesFor(deptId) || {}) : {};
  const deptWide = isAdmin || Boolean(caps.canViewDepartmentOrders);
  if (isHire) return `${deptWide ? '/department/hires' : '/hires'}?hire=${n.item_id}`;

  // 🔀 2026-10-09 (owner, demo run-through: a bell "poke to approve" opened the
  // old single order page — "Please load the order or hire in place"). An
  // ORDER opens EXPANDED in a list, never on `/orders/:id`:
  //   - can see department orders here → Department Orders (its row carries
  //     Approve/Reject for the step's approver too);
  //   - otherwise their OWN order → My Orders;
  //   - otherwise someone else's order, i.e. they are a step's approver → To Approve.
  // `order_user_id` comes from the bell's own query (`routes/notifications.js`,
  // `o.user_id`) — the order's owner, compared as strings (localStorage holds
  // TEXT, the API a NUMBER).
  if (deptWide) return `/department/orders?order=${n.order_id}`;
  const own = userId != null && userId !== '' && n.order_user_id != null
    && String(n.order_user_id) === String(userId);
  return own ? `/orders?order=${n.order_id}` : `/approvals?order=${n.order_id}`;
}

export default notificationTarget;
